import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import {
  isHousesEvent,
  isInterSchoolsEvent,
  isNonTeamSportEvent,
  isTeamOrTwoOnTwoSportEvent
} from 'Src/helpers/event/event';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import { PageHelpDefinition, PageHelpRegistration, registerPageHelpDefinition } from 'Src/helpers/pageHelp/pageHelp';
import { SchoolEventFromForm } from './SchoolEventManager';

type ParticipantsMode = 'new' | 'existing';

interface Props {
  mode: ParticipantsMode;
  user: AppUser;
  currentEvent: SchoolEventFromForm;
  availableCount: number;
  selectedCount: number;
  teamsCount: number;
  minTeams: number;
  maxTeams: number;
  isCreateEnabled: boolean;
  isAddTeamDisabled: boolean;
  isCurrentTeamPending: boolean;
  isCurrentTeamReady: boolean;
  teamName?: string;
  selectedTeamName?: string;
  houseName?: string;
  isEditingTeam?: boolean;
}

const scrollToParticipantsHelpAnchor = (anchorId: string): void => {
  const element = document.querySelector('[data-help-anchor="' + anchorId + '"]') as HTMLElement | null;
  if (!element) return;

  element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  const control = (element.querySelector('input') ||
    element.querySelector('select') ||
    element.querySelector('button')) as HTMLElement | null;
  if (control) window.setTimeout(() => control.focus(), 320);
};

export const SchoolEventParticipantsPageHelp: React.FunctionComponent<Props> = props => {
  const {
    mode,
    user,
    currentEvent,
    availableCount,
    selectedCount,
    teamsCount,
    minTeams,
    maxTeams,
    isCreateEnabled,
    isAddTeamDisabled,
    isCurrentTeamPending,
    isCurrentTeamReady,
    teamName = '',
    selectedTeamName = '',
    houseName = '',
    isEditingTeam = false
  } = props;
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const isExisting = mode === 'existing';
  const isIndividual = isNonTeamSportEvent(currentEvent);
  const isHouse = isHousesEvent(currentEvent);
  const isDirectSingleTeam = isInterSchoolsEvent(currentEvent) && !currentEvent.multiparty;
  const isStagingAvailable = !isIndividual && !isDirectSingleTeam;
  const requiresTeamName = !isIndividual && isTeamOrTwoOnTwoSportEvent(currentEvent);
  const sportName = propz.get(currentEvent, ['sport', 'name'], 'the selected activity');
  const gender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[currentEvent.gender] || currentEvent.gender || 'All genders';
  const ages =
    formatAgesCompact(currentEvent.ages || [], resolveAgeNamingFromContext(user, currentEvent)) || 'All ages';
  const selectedLabel = isIndividual ? 'participant' : 'player';
  const participantModeDescription = isExisting
    ? 'Pick a saved school team to reuse its normal squad. You can adjust the event copy without changing the saved team.'
    : isIndividual
    ? 'Select the students who will participate in this individual event.'
    : 'Build this event’s squad from eligible students. At final confirmation, decide whether to save the new team for reuse.';
  const teamsStillRequired = Math.max(minTeams - teamsCount, 0);
  const isTeamLimitReached = teamsCount >= maxTeams || isAddTeamDisabled;
  const createBlocker = (() => {
    if (isCreateEnabled) return '';
    if (isStagingAvailable && isCurrentTeamPending) {
      if (isEditingTeam) return 'Update the edited team in the fixture before creating the event.';
      if (isTeamLimitReached) {
        return `The maximum of ${maxTeams} ${
          maxTeams === 1 ? 'team is' : 'teams are'
        } already staged. Remove one before adding the current team.`;
      }
      if (!isCurrentTeamReady) {
        if (isExisting && !selectedTeamName) return 'Choose a saved team first.';
        if (requiresTeamName && !teamName.trim()) return 'Enter a team name.';
      }
      return 'Add the current team to the fixture before creating the event.';
    }
    if (isDirectSingleTeam && isExisting && !selectedTeamName && teamsCount === 0) {
      return 'Choose a saved team before creating the event.';
    }
    if (isDirectSingleTeam && !isExisting && requiresTeamName && !teamName.trim()) {
      return 'Enter a team name before creating the event.';
    }
    if (teamsStillRequired > 0) {
      return `Add ${teamsStillRequired} more ${
        teamsStillRequired === 1 ? 'team' : 'teams'
      } to meet the minimum of ${minTeams}.`;
    }
    return 'Complete the current team setup before creating the event.';
  })();
  const backChangesCount = teamsCount + (isCurrentTeamPending ? 1 : 0);

  const anchors = [
    ...(!isIndividual
      ? [
          {
            id: 'event-participants-mode',
            label: 'Build or reuse a team',
            description: isExisting
              ? `Pick an existing team is active. Switch to Build a new team when no suitable saved team exists. ${
                  isCurrentTeamPending
                    ? 'The current unadded selection is not staged; add it before switching if it must be kept.'
                    : 'Teams already staged in this fixture remain available after switching.'
                }`
              : `Build a new team is active. Switch to Pick an existing team to start with a saved school squad. ${
                  isCurrentTeamPending
                    ? 'The current unadded squad is not staged; add or update it before switching if it must be kept.'
                    : 'Teams already staged in this fixture remain available after switching.'
                }`
          }
        ]
      : []),
    ...(isHouse
      ? [
          {
            id: 'event-participants-house',
            label: 'House',
            description:
              'The selected house limits the available students. Each house can be added only once to this event.'
          }
        ]
      : []),
    ...(isExisting
      ? [
          {
            id: 'event-participants-team-picker',
            label: 'Existing team',
            description: selectedTeamName
              ? selectedTeamName + ' is selected. Its saved players have been copied into the event squad below.'
              : 'Search by team name. Results are restricted to teams compatible with this event’s sport, gender, ages and house.'
          }
        ]
      : requiresTeamName
      ? [
          {
            id: 'event-participants-team-name',
            label: 'Team name',
            description: teamName.trim()
              ? teamName.trim() + ' will identify this team in the event.'
              : 'A team name is required. Use a recognisable label such as First Team, A or Development.'
          }
        ]
      : []),
    {
      id: 'event-participants-students',
      label: isExisting ? 'Review the event squad' : isIndividual ? 'Choose participants' : 'Choose players',
      description: isExisting
        ? 'The right panel is a copy for this event. Adding or removing students here does not alter ' +
          (selectedTeamName || 'the saved team') +
          ' in the school team list.'
        : 'Move eligible students from Available students to Selected students when the squad is known. Players are optional and can be added later.'
    },
    ...(teamsCount > 0
      ? [
          {
            id: 'event-participants-added-teams',
            label: 'Teams in this fixture',
            description:
              teamsCount +
              ' ' +
              (teamsCount === 1 ? 'team has' : 'teams have') +
              ' been staged for the event. Nothing is created until you choose Create event.'
          }
        ]
      : []),
    ...(isStagingAvailable
      ? [
          {
            id: 'event-participants-stage-team',
            label: isEditingTeam ? 'Update team in fixture' : 'Add team to fixture',
            description: isCurrentTeamReady
              ? isEditingTeam
                ? 'The edited squad is ready. Update it before creating the event or switching modes.'
                : 'The current squad is ready to be staged in this event.'
              : isTeamLimitReached && !isEditingTeam
              ? `Unavailable because this event has reached its limit of ${maxTeams} ${
                  maxTeams === 1 ? 'team' : 'teams'
                }.`
              : isExisting && !selectedTeamName
              ? 'Choose a saved team first.'
              : requiresTeamName && !teamName.trim()
              ? 'Enter a team name.'
              : 'The team is ready to be added; players can be added later.'
          }
        ]
      : []),
    {
      id: 'event-participants-actions',
      label: 'Back or create',
      description: `Back to details returns to step 1 and clears ${
        backChangesCount > 0 ? 'the staged/current team work in step 2' : 'the current step-2 editor'
      }. ${isCreateEnabled ? 'Create event is available now.' : `Create event is unavailable: ${createBlocker}`}`
    }
  ];

  const definition: PageHelpDefinition = {
    screenId: isExisting ? 'school.events.create.participants.existing' : 'school.events.create.participants.new',
    title: isExisting
      ? 'Create an event — choose a saved team'
      : isIndividual
      ? 'Create an event — select participants'
      : 'Create an event — build teams',
    subtitle: 'Step 2: ' + participantModeDescription,
    entity: { singular: isIndividual ? 'participant' : 'team', plural: isIndividual ? 'participants' : 'teams' },
    useDefaultSelectionObservation: false,
    observations: [
      {
        id: 'event-participant-eligibility',
        tone: 'neutral',
        title: sportName + ' · ' + gender + ' · ' + ages,
        text:
          availableCount.toLocaleString('en-GB') +
          ' eligible ' +
          (availableCount === 1 ? 'student is' : 'students are') +
          ' currently available. Search and filters can narrow this list; they do not change the event’s eligibility rules from step 1.'
      },
      {
        id: 'event-participant-progress',
        tone:
          isExisting && !selectedTeamName
            ? 'warning'
            : requiresTeamName && !teamName.trim()
            ? 'warning'
            : selectedCount > 0
            ? 'positive'
            : 'neutral',
        title: isExisting
          ? selectedTeamName || 'Choose a saved team'
          : isIndividual
          ? selectedCount + ' ' + (selectedCount === 1 ? 'participant' : 'participants') + ' selected'
          : teamName.trim() || 'Name this team',
        text: isExisting
          ? selectedTeamName
            ? selectedCount +
              ' ' +
              (selectedCount === 1 ? 'player is' : 'players are') +
              ' in the event copy. Changes here leave the saved school team unchanged.'
            : 'Start typing a team name and select a compatible result before reviewing its squad.'
          : requiresTeamName && !teamName.trim()
          ? 'Enter a team name. You can add players now or leave the squad empty and complete it later.'
          : selectedCount +
            ' ' +
            (selectedCount === 1 ? selectedLabel : selectedLabel + 's') +
            ' selected' +
            (isEditingTeam ? '; you are updating a team already staged for this fixture' : '') +
            '.'
      },
      ...(!isIndividual
        ? [
            {
              id: 'event-participant-team-limits',
              tone: isDirectSingleTeam
                ? isCreateEnabled
                  ? ('positive' as const)
                  : ('warning' as const)
                : isTeamLimitReached
                ? ('neutral' as const)
                : teamsStillRequired > 0
                ? ('warning' as const)
                : ('positive' as const),
              title: isDirectSingleTeam
                ? 'One current team required'
                : `${teamsCount} staged · minimum ${minTeams} · maximum ${maxTeams}`,
              text: isDirectSingleTeam
                ? 'This event uses one current team. It is included directly when you choose Create event.'
                : isTeamLimitReached
                ? 'The team limit has been reached. Review the staged teams, then create the event.'
                : teamsStillRequired > 0
                ? `${teamsStillRequired} more ${
                    teamsStillRequired === 1 ? 'team is' : 'teams are'
                  } required before creation.`
                : 'The minimum team count is satisfied. You may add another team up to the maximum, or create the event when no team is left unstaged.'
            }
          ]
        : []),
      {
        id: 'event-participant-create-state',
        tone: isCreateEnabled ? 'positive' : 'warning',
        title: isCreateEnabled ? 'Ready to create the event' : 'The event is not ready yet',
        text: isCreateEnabled
          ? isExisting
            ? 'Create event uses the adjusted event copy; the original saved team remains unchanged.'
            : isIndividual
            ? 'Review the selected participants, then create the event.'
            : 'Create event will ask which newly built teams should also be saved to the school team list.'
          : createBlocker
      }
    ],
    anchors,
    actions: [
      ...(isExisting
        ? [
            {
              id: 'event-help-find-team',
              label: selectedTeamName ? 'Review selected team' : 'Find a saved team',
              description: selectedTeamName
                ? 'Go to the selected team and its event squad.'
                : 'Go to team search. Enter part of the saved team name and choose a result.',
              kind: selectedTeamName ? ('secondary' as const) : ('primary' as const),
              anchorId: selectedTeamName ? 'event-participants-students' : 'event-participants-team-picker',
              run: () =>
                scrollToParticipantsHelpAnchor(
                  selectedTeamName ? 'event-participants-students' : 'event-participants-team-picker'
                )
            }
          ]
        : [
            {
              id: 'event-help-select-participants',
              label: isIndividual ? 'Choose participants' : 'Build the squad',
              description:
                'Go to the two-panel selector. Search or filter on the left, then move students to the selected list.',
              kind: selectedCount > 0 ? ('secondary' as const) : ('primary' as const),
              anchorId: 'event-participants-students',
              run: () => scrollToParticipantsHelpAnchor('event-participants-students')
            }
          ]),
      ...(!isIndividual && teamsCount > 0
        ? [
            {
              id: 'event-help-review-added-teams',
              label: 'Review teams in fixture',
              description: 'Go to the teams already staged for this event. Edit or remove them before creation.',
              kind: 'secondary' as const,
              anchorId: 'event-participants-added-teams',
              run: () => scrollToParticipantsHelpAnchor('event-participants-added-teams')
            }
          ]
        : []),
      ...(isStagingAvailable && isCurrentTeamPending
        ? [
            {
              id: 'event-help-stage-current-team',
              label: isEditingTeam ? 'Update the current team' : 'Add the current team',
              description: isCurrentTeamReady
                ? isEditingTeam
                  ? 'Apply the edited squad to Teams in this fixture before continuing.'
                  : 'Stage this squad under Teams in this fixture before continuing.'
                : createBlocker,
              kind: 'primary' as const,
              anchorId: 'event-participants-stage-team',
              run: () => scrollToParticipantsHelpAnchor('event-participants-stage-team')
            }
          ]
        : []),
      {
        id: 'event-help-create-event',
        label: isCreateEnabled ? 'Review and create' : 'Why can’t I create it?',
        description: isCreateEnabled ? 'Go to the final actions after reviewing the participant setup.' : createBlocker,
        kind: isCreateEnabled ? 'primary' : 'secondary',
        anchorId: 'event-participants-actions',
        run: () => scrollToParticipantsHelpAnchor('event-participants-actions')
      },
      {
        id: 'event-help-back-details',
        label: 'Back to event details',
        description:
          backChangesCount > 0
            ? 'This returns to step 1 and clears teams and current squad work from step 2. Use it only when the event details must change.'
            : 'Return to step 1 to change activity, gender, ages, event type, opponents or venue.',
        kind: 'secondary',
        anchorId: 'event-participants-actions',
        run: () => scrollToParticipantsHelpAnchor('event-participants-actions')
      }
    ],
    sections: [
      {
        id: 'event-help-participant-actions',
        title: 'Available actions now',
        items: [
          isIndividual
            ? `Create event is available ${
                selectedCount === 0
                  ? 'without participants; you can add them later'
                  : `with ${selectedCount} selected ${selectedCount === 1 ? 'participant' : 'participants'}`
              }.`
            : isCreateEnabled
            ? 'Create event is available. It includes every team under Teams in this fixture and, for a direct single-team event, the current team.'
            : `Create event is unavailable: ${createBlocker}`,
          isStagingAvailable
            ? isCurrentTeamReady
              ? `${
                  isEditingTeam ? 'Update team in fixture' : 'Add team to fixture'
                } is available for the current squad.`
              : `${
                  isEditingTeam ? 'Update team in fixture' : 'Add team to fixture'
                } is unavailable until the current squad is complete.`
            : 'This event does not use a separate Add team to fixture action; the current selection is included directly.',
          !isIndividual
            ? `You can switch between Build a new team and Pick an existing team. Staged teams remain, but an unadded current selection does not.`
            : 'Participant mode cannot be switched because this activity uses individual participants.',
          backChangesCount > 0
            ? 'Back to details clears the teams and current squad work from step 2.'
            : 'Back to details returns to step 1; there is no staged team work to clear yet.'
        ],
        anchorIds: ['event-participants-actions', 'event-participants-mode']
      },
      {
        id: 'event-help-participant-eligibility',
        title: 'Who appears in Available students',
        items: [
          'Sport, gender and age eligibility come from event details on step 1. Use Back to details when those rules are wrong.',
          isHouse
            ? 'This is a house event, so the selected house' +
              (houseName ? ' (' + houseName + ')' : '') +
              ' also limits the available students.'
            : 'Students already staged in another team for this event are excluded from the available list.',
          'Search and filters only narrow the eligible results. They do not make an ineligible student available.',
          'Where your school enables event-clash checks, use the clash indicator to review overlapping commitments before selecting a student.'
        ],
        anchorIds: ['event-participants-students']
      },
      {
        id: 'event-help-moving-students',
        title: 'Move and review students',
        items: [
          'Select rows on the left and move them to Selected students, or double-click a row to move it immediately.',
          'Use the selected-list controls to remove a student again before creating the event.',
          'The top checkbox offers Select this page, Select all and Clear selection; use Select all carefully when filters are active.'
        ],
        anchorIds: ['event-participants-students']
      },
      ...(isExisting
        ? [
            {
              id: 'event-help-existing-team-copy',
              title: 'What happens to the saved team',
              items: [
                'Team search returns saved teams compatible with the event sport, gender, ages and, for a house event, the selected house.',
                'Selecting a team copies its players into this event. Adding or removing players here changes only the event copy.',
                'The saved team in Sport & Fixtures → Teams is not overwritten by these adjustments.'
              ],
              anchorIds: ['event-participants-team-picker', 'event-participants-students']
            }
          ]
        : isIndividual
        ? []
        : [
            {
              id: 'event-help-new-team-save',
              title: 'Add one or several new teams',
              items: [
                'For an event that allows several teams, finish the current team and choose Add team to fixture before building the next one.',
                'Teams shown under Teams in this fixture are staged only; click a row to edit it or remove it before creating the event.',
                `This event requires at least ${minTeams} and allows at most ${maxTeams} ${
                  maxTeams === 1 ? 'team' : 'teams'
                }.`,
                'When you create the event, Squad In Touch asks which newly built teams should also be saved in the school team list for future use.'
              ],
              anchorIds: ['event-participants-team-name', 'event-participants-added-teams']
            }
          ])
    ]
  };

  React.useEffect(() => {
    registrationRef.current = registerPageHelpDefinition(definition);
    return () => {
      if (registrationRef.current) registrationRef.current.unregister();
      registrationRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (registrationRef.current) registrationRef.current.update(definition);
  });

  return null;
};
